import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'

import {
  PriceTrendsHistoryResponse,
  PriceTrendsParams
} from '../services/priceInsightsApi'
import {PriceTrendInsightsPayload} from '../types'

/**
 * Minimum number of days required for displaying price trend insights
 */
export const MIN_DAYS_FOR_INSIGHTS = 7

/**
 * Minimum number of days required for rendering the price history chart
 */
export const MIN_DAYS_FOR_CHART = 15

/**
 * Validates if the insights data meets the minimum day requirement for display.
 * Price trends require at least 7 days of data to provide meaningful insights.
 *
 * @param insights - Price trend insights payload
 * @returns true if insights have sufficient data (≥7 days), false otherwise
 */
export const hasMinimumDaysForInsights = (
  insights: PriceTrendInsightsPayload | undefined | null
): insights is PriceTrendInsightsPayload => {
  if (!insights) return false
  return insights.timeRangeDays >= MIN_DAYS_FOR_INSIGHTS
}

/**
 * Validates if the price history data meets the minimum day requirement for chart rendering.
 * Charts require at least 15 days of data to provide meaningful visualization.
 *
 * @param priceHistory - Price history response data
 * @returns true if price history has sufficient data (≥15 days), false otherwise
 */
export const hasMinimumDaysForChart = (
  priceHistory: PriceTrendsHistoryResponse | undefined | null
): priceHistory is PriceTrendsHistoryResponse => {
  if (!priceHistory) return false
  return priceHistory.timeRangeDays >= MIN_DAYS_FOR_CHART
}

/**
 * Validates if price history data has the required hotel price data points.
 *
 * @param priceHistory - Price history response data
 * @returns true if hotel history array exists and has data points, false otherwise
 */
export const hasValidHotelHistory = (
  priceHistory: PriceTrendsHistoryResponse | undefined | null
): boolean => {
  if (!priceHistory) return false
  return (
    Array.isArray(priceHistory.hotelHistory) &&
    priceHistory.hotelHistory.length > 0
  )
}

/**
 * Validates if the insights data has valid median hotel price.
 *
 * @param insights - Price trend insights payload
 * @returns true if median hotel price is a valid positive number, false otherwise
 */
export const hasValidMedianHotelPrice = (
  insights: PriceTrendInsightsPayload | undefined | null
): boolean => {
  if (!insights) return false
  return (
    typeof insights.medianHotelPrice === 'number' &&
    Number.isFinite(insights.medianHotelPrice) &&
    insights.medianHotelPrice > 0
  )
}

/**
 * Comprehensive validation to determine if insights are ready for display.
 * Checks for:
 * - Non-null insights
 * - Minimum 7 days of data
 * - Valid median hotel price
 *
 * @param insights - Price trend insights payload
 * @returns true if all validations pass, false otherwise
 */
export const isInsightsDataValid = (
  insights: PriceTrendInsightsPayload | undefined | null
): insights is PriceTrendInsightsPayload => {
  return (
    hasMinimumDaysForInsights(insights) && hasValidMedianHotelPrice(insights)
  )
}

/**
 * Comprehensive validation to determine if chart can be rendered.
 * Checks for:
 * - Non-null price history
 * - Minimum 15 days of data
 * - Valid hotel history data points
 *
 * @param priceHistory - Price history response data
 * @returns true if all validations pass, false otherwise
 */
export const isChartDataValid = (
  priceHistory: PriceTrendsHistoryResponse | undefined | null
): priceHistory is PriceTrendsHistoryResponse => {
  return (
    hasMinimumDaysForChart(priceHistory) && hasValidHotelHistory(priceHistory)
  )
}

/**
 * Checks if the room configuration is eligible for price insights.
 * Price insights API only supports:
 * - Single room searches (1 room)
 * - Adult-only searches (no children)
 *
 * @param rooms - Room configuration string (e.g., "2" for 2 adults, "2|3" for multiple rooms)
 * @returns true if eligible for price insights, false otherwise
 */
export const isEligibleForPriceInsights = (
  rooms: string | undefined
): boolean => {
  if (!rooms) return false

  const config = roomsToConfigurationObject(rooms)

  return config.numberOfRooms === 1 && config.numberOfChildren === 0
}

/**
 * Normalizes and validates fetch parameters for price trend API requests.
 * Ensures all required parameters are present before making API calls.
 *
 * @param rawParams - Raw fetch parameters that may be incomplete
 * @returns Normalized parameters if all required fields are present, undefined otherwise
 */
export const normalizeFetchParams = (
  rawParams?: Partial<PriceTrendsParams>
): Partial<PriceTrendsParams> | undefined => {
  if (!rawParams) {
    return undefined
  }

  const {hotelId, checkIn, checkOut, rooms, currency} = rawParams

  // Since params now come from getSearchBoxValues (typed selectors),
  // we only need to check for presence, not types
  if (!hotelId || !checkIn || !checkOut || !rooms || !currency) {
    return undefined
  }

  return {
    hotelId,
    checkIn,
    checkOut,
    rooms,
    currency
  }
}
